<template>
  <div v-show="displayFrontend" :class="{questions__save: safeFreeze}" class="questions">
    <h1 class="questions__header">{{ $t("Leave") }}</h1>
    <h2 v-show="contractsEmpty" class="questions__subheader questions__inputerrordescription">
      {{ $t("Please fill in the contract details first to proceed to this page.") }}</h2>

    <div v-if="!contractsEmpty">
      <h2 class="questions__subheader">{{ $t("Indicate below the contract types on which leave is accrued.") }}</h2>
      <div v-for="(contractType) in contract.contractTypes" v-if="contractType.active">
        <b-form-checkbox
          v-model="contractType.saveLeaveAmount"
          :disabled="readOnly"
          name="check-button"
          switch>
          {{ contractType.displayName }}
        </b-form-checkbox>
      </div>

      <h2 class="questions__subheader">{{
          $t('How many hours of leave does an employee with a full-time work week ({ftHours} hours) get per year', {ftHours: contract.fulltimeHours})
        }}</h2>
      <input
        v-model="contract.holidayHours"
        :disabled="readOnly"
        class="form-control"
        min="0"
        type="number"
        @change="calculateRelativeHoliday()"
        @click="calculateRelativeHoliday()"
      />

      <h2 class="questions__subheader">{{ $t("What is the vacation accrual percentage?") }}</h2>
      <input
        v-model="contract.holidayPercentage"
        :disabled="readOnly"
        class="form-control"
        min="0"
        type="number"
      />

      <p class="questions__teammember" v-show="multipleSaveLeaveAmount">{{$t("If the vacation accrual percentage is not equal for all contract types, please indicate this the remark field below.")}}</p>

      <div v-show="overtime.overtimePay !== 'overtimeManualPlusHoursTft'">
        <p class="questions__teammember" v-show="multipleSaveLeaveAmount">{{$t("Does leave accrual have to take place over the paid additional hours (if applicable)?")}}</p>
        <b-form-checkbox
          v-model="contract.annualLeaveOnAdditionalHours"
          :disabled="readOnly"
          name="check-button"
          switch>
          {{ $t("Yes") }}
        </b-form-checkbox>
      </div>

      <p class="questions__teammember">{{
          $t("Based on the accrual of vacation and future withdrawals, we can show the predicted annual balance to employees and administrators. The prediction is made until the end of the contract and the end of the year. The predicted accrual is calculated as follows: ((contract hours / 7) * number of calendar days) * accrual rate.")
        }}</p>

      <h2 class="questions__subheader">{{
          $t("Do you also want to track vacation due dates based on statutory and excess hours based on the predicted annual balance?")
        }}</h2>
      <b-form-checkbox
        v-model="contract.keepAnnualBalanceLawfull"
        :disabled="readOnly"
        name="check-button"
        switch>
        {{ $t("Yes, we want to keep track of vacation due dates in Dyflexis") }}
      </b-form-checkbox>

      <h2 class="questions__subheader">{{ $t("Does your organization use reduced work hours?") }}</h2>
      <b-form-checkbox
        v-model="shortTimeWorkingEnabled"
        :disabled="readOnly"
        name="check-button"
        switch
        @change="contract.amountShortTimeWorkingHours=null">
        {{ $t("Yes, we use reduced working hours") }}
      </b-form-checkbox>

      <div v-show="shortTimeWorkingEnabled">
        <h2 class="questions__subheader">{{
            $t("How many reduced working hours do full-time employees get per year?")
          }}</h2>

        <b-form-textarea
          v-model="contract.amountShortTimeWorkingHours"
          :disabled="readOnly"
          class="questions__textarea"
          rows="3"/>
      </div>

      <h2 class="questions__subheader">{{ $t("Should accrual of leave be calculated for hours between min and max?") }}</h2>
      <b-form-checkbox
        v-model="contract.minMaxAccrualCalculation"
        :disabled="readOnly"
        name="check-button"
        switch>
        {{ $t("Yes, min-max contracts should accrual leave") }}
      </b-form-checkbox>

      <h2 class="questions__subheader">{{ $t("Can the employee choose the leave type during the leave request?") }}</h2>
      <b-form-checkbox
        v-model="contract.allowLeaveTypeSelection"
        :disabled="readOnly"
        name="check-button"
        switch>
        {{ $t("Yes, the employee can select the leave type") }}
      </b-form-checkbox>

      <p v-show="this.setupPackage === 'Premium' || this.setupPackage === 'Enterprise'"><br><i>{{ $t("Extra saldi info text") }}</i></p>

      <h2 class="questions__subheader">{{ $t("Remarks") }}</h2>
      <b-form-textarea
        v-model="contract.leaveRemarks"
        :disabled="readOnly"
        class="questions__textarea"
        no-resize
        rows="3"/>

      <input :value="$t('Save')" type="button" @click="handleSave"/>
      <p v-show="showSaveStatus" class="questions--savesuccess">{{ $t("Saved successfully!") }}</p>
    </div>
  </div>
</template>

<script>
import SystemRequestService from "@/SystemRequestService";
import GlobalFunctions from "@/GlobalFunctions";
import router from "@/router";

export default {
  name: "Leave",
  data() {
    return {
      displayFrontend: false,
      safeFreeze: false,
      readOnly: false,
      showSaveStatus: false,
      contractsEmpty: true,
      shortTimeWorkingEnabled: false,
      setupPackage: null,
      minMaxEnabled: false,
      displayStep: 1,
      contract: null,
      overtime: null
    };
  },
  async beforeMount() {
    const result = await SystemRequestService.getProject(this.$route.params.hashedId);
    if (!result) {
      alert(this.$t("Error while obtaining your details. Please try again later or contact your Dyflexis consultant."));
      return false;
    }

    if (result.contracts === null) {
      this.displayFrontend = true;
      return false;
    }

    let contractDecoded;
    try {
      contractDecoded = JSON.parse(result.contracts.replaceAll("&quot;", '"'));
    } catch (e) {
      alert(this.$t("Error while obtaining your details. Please try again later or contact your Dyflexis consultant."));
      this.displayFrontend = true;
      return false;
    }
    this.contract = contractDecoded;

    this.contract.contractTypes.forEach(
      contract => {
        if (contract.active) {
          this.contractsEmpty = false;
        }
      }
    );

    if (this.contract.amountShortTimeWorkingHours != null) {
      this.shortTimeWorkingEnabled = true;
    }

    if (this.contract.minMaxDescription !== null) {
      this.minMaxEnabled = true;
    }

    if (result.overtime !== null) {
      let overtimeDecoded;
      try {
        overtimeDecoded = JSON.parse(result.overtime.replaceAll("&quot;", '"'));
      } catch (e) {
        this.displayFrontend = true;
        return false;
      }
      this.overtime = overtimeDecoded;
    }

    this.setupPackage = result.setupPackage;
    this.readOnly = result.isSigned;
    GlobalFunctions.setReadOnly(result.isSigned);
    localStorage.setItem('startingObject', JSON.stringify(this.contract));
    this.displayFrontend = true;
  },
  beforeRouteLeave: function (to, from, next) {
    if (!GlobalFunctions.acceptPageLeave(this.contract, this.$t("You have made changes without saving them. Press cancel to ignore these changes and leave the page."))) {
      this.handleSave(false);
    }
    next();
  },
  methods: {
    multipleSaveLeaveAmount() {
      let count = 0;
      this.contract.contractTypes.forEach(
        contractType => {
          if (contractType.active && contractType.saveLeaveAmount) {
            count += 1;
            if (count > 1) {
              return true;
            }
          }
        }
      );
      return false;
    },
    calculateRelativeHoliday() {
      this.contract.holidayPercentage = (this.contract.holidayHours / 52 * 100 / this.contract.fulltimeHours).toFixed(5);
    },
    async handleSave(saveInitialObject) {
      this.safeFreeze = true;
      const result = await SystemRequestService.updateProject(this.contract, this.$route.params.hashedId);
      if (!result || result.status !== 'success') {
        alert(this.$t("Error while updating, please send a screenshot of the fields on this page to your Dyflexis consultant!"));
        return;
      }

      if (saveInitialObject !== false) {
        localStorage.setItem('startingObject', JSON.stringify(this.contract));
      }

      this.safeFreeze = false;
      this.showSaveStatus = true;

      setTimeout(() => {
        this.showSaveStatus = false;
      }, 3000);
    },
  }
};
</script>
